/**
 * The fleet's colour contract, repointed at the Alif & Lakshita palette.
 *
 * Every template in the fleet exposes these same names, and the inherited
 * sections and common components all read them. Keeping the names and
 * changing only what they resolve to moves the whole invitation onto the new
 * palette in one edit, and the team's existing knowledge of these constants
 * still applies.
 *
 * The values themselves live in `./design-tokens`. Nothing here should
 * introduce a colour of its own.
 */
import { COLORS, FONTS, TYPE_SCALE } from './design-tokens';
import { TEXT_PROPS } from './default-props';

// Mood Board Color
export const BG_PRIMARY = COLORS.base;
export const BG_SECONDARY = COLORS.olive;
export const BG_ALTERNATIVE = COLORS.oliveLight;
export const BG_LAYER = 'rgba(20, 26, 20, 0.72)';

// Text Color
export const TEXT_PRIMARY = COLORS.text;
export const TEXT_SECONDARY = COLORS.text;
export const TEXT_ALTERNATIVE = COLORS.textMuted;
export const TEXT_BUTTON = COLORS.text;

export const ASSET_COLOR = COLORS.asterisk;

// Music & Navigation Color
export const NAVIGATION_COLOR = 'rgba(20, 26, 20, 0.82)';
export const ICON_NAVIGATION_COLOR = COLORS.text;

// Default Button Color
export const BUTTON_PROPS = {
  _hover: { opacity: '.8' },
};

/**
 * THE OUTLINED PILL — the design's only button. Transparent fill, hairline
 * border, italic serif label.
 *
 * This is its SINGLE definition. Every button in the invitation reaches it by
 * spreading this name, so changing it here restyles all of them without editing
 * a single call site. The name itself is inherited from the fleet, which is why
 * it reads as being about padding when it describes the whole pill.
 *
 * WHY IT LIVES HERE and not in `./design-tokens`, where the rest of the visual
 * language is declared: it needs `TEXT_PROPS.body`, and `./default-props` already
 * imports from `./design-tokens`. Declaring the pill upstream would form the
 * import cycle that `./default-props` warns about, so it has to sit downstream of
 * both. A near-copy called `PILL_PROPS` once sat in design-tokens, documented as
 * canonical but imported by nothing — almost certainly for this reason. It has
 * been removed rather than wired up.
 *
 * `fontWeight` arrives from the `TEXT_PROPS.body` spread as the THEME TOKEN NAME
 * rather than a literal `400`, and must stay that way: the live-simulation drawer
 * (`@invitato/imagination-room`) rewrites the theme's font weights at runtime,
 * and a literal would stop following it. It is the only value from that spread
 * which survives — every other one is overridden below.
 */
export const BUTTON_PADDING_PROPS = {
  ...TEXT_PROPS.body,
  /**
   * The hover fill and (from the traveling-highlight work) the border trace.
   * Defined in `src/components/Layout/assets/styles.css`; carried here so that
   * every site spreading this token gets the effect with no call-site edit, and
   * so a new button cannot be added without it.
   *
   * `data-button` is what makes buttons enumerable in a rendered snapshot.
   * `scripts/verify-build.js` asserts that everything carrying it also carries
   * the class — the drift it catches is a call site passing its own
   * `className`, which REPLACES this one rather than merging with it. That is
   * not hypothetical: ScrollToDown/V1 did exactly that.
   */
  className: 'ivt-btn',
  'data-button': 'pill',
  variant: 'unstyled',
  fontFamily: FONTS.body,
  fontStyle: 'italic',
  fontSize: 'calc(var(--chakra-fontSizes-body) - 2px)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '18px 32px',
  borderRadius: '999px',
  background: 'transparent',
  border: '1px solid',
  borderColor: COLORS.line,
  color: COLORS.text,
  boxShadow: 'none',
  position: 'relative',
  zIndex: '20',
  whiteSpace: 'nowrap',
  transition: 'background-color .2s ease, border-color .2s ease',
  _hover: { background: 'rgba(255,255,255,0.10)', borderColor: COLORS.text },
  _active: { background: 'rgba(255,255,255,0.16)' },
  _focusVisible: { outline: '2px solid', outlineColor: COLORS.text, outlineOffset: '2px' },
};

export const DEFAULT_BUTTON_PROPS = {
  fontWeight: 'normal',
};
